/**
 * ImportWizard step panels.
 * @layer widget
 * @package @plerity/dashboard-shared
 *
 * Literal Tailwind gray ramp with a `dark:` pair for every colour (no CSS vars: the SPA Tailwind v4 build
 * strips them). Never a `-950` shade.
 */

import { type CsvRowIssue, downloadCsv } from "@plerity/csv";
import type { ChangeEvent, ReactNode } from "react";
import type {
  BatchTotals,
  ImportDuplicateMatch,
  ImportRowDecision,
  ImportWizardTarget,
  ServerRowIssue,
} from "./ImportWizard.types";
import { allowedDecisions, type CsvImportController } from "./useCsvImport";

// ---------------------------------------------------------------------------
// Shared classes + small pieces
// ---------------------------------------------------------------------------

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800";
const BUTTON = `inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`;
export const BUTTON_PRIMARY = `${BUTTON} bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500`;
export const BUTTON_SECONDARY = `${BUTTON} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700`;
const SELECT = `w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 ${FOCUS}`;
const MUTED = "text-gray-600 dark:text-gray-400";
const BODY = "text-gray-700 dark:text-gray-300";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400";
const TD = "px-3 py-2 align-top text-sm text-gray-700 dark:text-gray-300";

const DELIMITER_NAMES: Record<string, string> = { ",": "comma", ";": "semicolon", "\t": "tab", "|": "pipe" };

const n = (value: number): string => value.toLocaleString("en-US");
const plural = (count: number, word: string): string => `${n(count)} ${word}${count === 1 ? "" : "s"}`;

function Chip({ tone, children }: { tone: "good" | "bad" | "warn" | "neutral"; children: ReactNode }) {
  const tones = {
    good: "bg-green-100 text-success-strong dark:bg-green-900/40",
    bad: "bg-red-100 text-danger-strong dark:bg-red-900/40",
    warn: "bg-amber-100 text-warning-strong dark:bg-amber-900/40",
    neutral: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  } as const;
  return <span className={`rounded-full px-3 py-1 text-sm font-medium ${tones[tone]}`}>{children}</span>;
}

function ErrorPanel({ title, message, children }: { title: string; message: string; children?: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-danger-strong dark:border-red-700 dark:bg-red-900/20"
    >
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{message}</p>
      {children !== undefined && <div className="mt-3 flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

function Progress({ label, value, max = 1 }: { label: string; value: number; max?: number }) {
  return (
    <div className="space-y-1">
      <p className={`text-sm ${BODY}`}>{label}</p>
      {/* max=0 means "unknown length": render the indeterminate (no value) form. */}
      <progress
        className="h-2 w-full accent-blue-600 dark:accent-blue-400"
        aria-label={label}
        value={max === 0 ? undefined : value}
        max={max === 0 ? undefined : max}
      />
    </div>
  );
}

type FieldLabels = ReadonlyMap<string, string>;

interface ProblemRow {
  key: string;
  row: number;
  field: string | null;
  message: string;
}

function ProblemTable({
  caption,
  problems,
  fieldLabels,
  limit = 100,
}: {
  caption: string;
  problems: ProblemRow[];
  fieldLabels: FieldLabels;
  limit?: number;
}) {
  const shown = problems.slice(0, limit);
  return (
    <div className="space-y-2">
      <div className="max-h-72 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" className={TH}>
                Row
              </th>
              <th scope="col" className={TH}>
                Column
              </th>
              <th scope="col" className={TH}>
                Problem
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {shown.map((problem) => (
              <tr key={problem.key}>
                <td className={TD}>{problem.row}</td>
                <td className={TD}>
                  {problem.field === null ? "Whole row" : (fieldLabels.get(problem.field) ?? problem.field)}
                </td>
                <td className={`${TD} text-danger-strong`}>{problem.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {problems.length > shown.length && (
        <p className={`text-xs ${MUTED}`}>
          Showing the first {n(shown.length)} of {n(problems.length)} problems. Download the report to see them all.
        </p>
      )}
    </div>
  );
}

function issueRows(issues: ReadonlyArray<CsvRowIssue>): ProblemRow[] {
  return issues.map((issue, i) => ({
    key: `${issue.row}-${issue.field ?? "row"}-${i}`,
    row: issue.row,
    field: issue.field,
    message: issue.message,
  }));
}

function serverRows(issues: ReadonlyArray<ServerRowIssue>): ProblemRow[] {
  return issues.map((issue, i) => ({
    key: `s-${issue.row}-${i}`,
    row: issue.row,
    field: issue.field ?? null,
    message: issue.message,
  }));
}

function downloadReport(fileName: string, problems: ProblemRow[], fieldLabels: FieldLabels): void {
  downloadCsv(
    `${fileName.replace(/\.[^.]+$/, "")}-problems`,
    ["Row", "Column", "Problem"],
    problems.map((p) => [p.row, p.field === null ? "Whole row" : (fieldLabels.get(p.field) ?? p.field), p.message])
  );
}

interface StepProps<T> {
  c: CsvImportController<T>;
  target: ImportWizardTarget<T>;
}

function fieldLabelMap<T>(target: ImportWizardTarget<T>): FieldLabels {
  return new Map(target.fields.map((field) => [field.key, field.label]));
}

// ---------------------------------------------------------------------------
// Step 1 — upload
// ---------------------------------------------------------------------------

export function UploadStep<T>({ c, target, maxFileBytes }: StepProps<T> & { maxFileBytes: number }) {
  const { upload } = c.state;
  const reading = upload.status === "reading";

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void c.selectFile(file);
  };

  const downloadTemplate = () => {
    const sample = target.sample ?? [];
    downloadCsv(
      `${target.entityName.replace(/\s+/g, "-")}-template`,
      target.fields.map((field) => field.label),
      sample.map((row) => target.fields.map((field) => row[field.key] ?? ""))
    );
  };

  return (
    <div className="space-y-4">
      <p className={`text-sm ${BODY}`}>
        Upload a CSV file of {target.entityName}. The first row should hold the column names. You will match the columns
        and check every row before anything is saved.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label
          className={`inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus-within:ring-offset-gray-800 ${reading ? "opacity-50" : ""}`}
        >
          {reading ? "Reading file..." : "Choose CSV file"}
          <input
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            className="sr-only"
            disabled={reading}
            onChange={onChange}
            data-testid="csv-file-input"
          />
        </label>
        <button type="button" className={BUTTON_SECONDARY} onClick={downloadTemplate}>
          Download template
        </button>
      </div>
      <p className={`text-xs ${MUTED}`}>
        Up to {n(Math.round(maxFileBytes / 1024 / 1024))} MB. Commas, semicolons, tabs and pipes are detected
        automatically. UTF-8 and Excel (Windows) encodings both work.
      </p>
      {reading && <Progress label="Reading the start of your file" value={0} max={0} />}
      {upload.status === "error" && upload.message !== null && (
        <ErrorPanel title="That file could not be used" message={upload.message}>
          <span className={`text-xs ${MUTED}`}>Choose a different file to try again.</span>
        </ErrorPanel>
      )}
      <details className="text-sm">
        <summary className={`cursor-pointer font-medium text-blue-700 dark:text-blue-300 ${FOCUS} rounded`}>
          Tips for a clean import
        </summary>
        <ul className={`mt-2 list-disc space-y-1 pl-5 ${BODY}`}>
          <li>Put the column names in the first row.</li>
          <li>Wrap a value that contains a comma in double quotes, for example "Smith, Jo".</li>
          <li>Cells that start with = + - or @ are flagged, because a spreadsheet could run them as formulas.</li>
          <li>Rows with problems are listed with their row number so you can fix them in your spreadsheet.</li>
        </ul>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — map columns
// ---------------------------------------------------------------------------

export function MapStep<T>({ c, target }: StepProps<T>) {
  const { state } = c;
  const { preview, headers, mapping, renamed, hasHeader } = state;
  const sampleRows = (preview?.rows ?? []).slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 3);
  const fieldLabels = fieldLabelMap(target);

  return (
    <div className="space-y-4">
      <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm ${BODY}`}>
        <span>
          Detected a <strong>{DELIMITER_NAMES[preview?.delimiter ?? ","] ?? "comma"}</strong>-separated file in{" "}
          <strong>{preview?.encoding ?? "utf-8"}</strong>
          {preview?.hadBom ? " (byte-order mark removed)" : ""}.
        </span>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={hasHeader}
            onChange={(event) => c.setHasHeader(event.target.checked)}
            className={`h-4 w-4 rounded border-gray-300 dark:border-gray-600 ${FOCUS}`}
          />
          First row contains column names
        </label>
      </div>

      {renamed.length > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-warning-strong dark:border-amber-700 dark:bg-amber-900/20">
          {plural(renamed.length, "column")} had an empty or repeated name and {renamed.length === 1 ? "was" : "were"}{" "}
          renamed: {renamed.map((r) => `"${r.to}"`).join(", ")}.
        </p>
      )}

      <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full">
          <caption className="sr-only">Match each column in your file to a field</caption>
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" className={TH}>
                Column in your file
              </th>
              <th scope="col" className={TH}>
                Example values
              </th>
              <th scope="col" className={TH}>
                Import as
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {mapping.map((entry) => {
              const examples = sampleRows.map((row) => row[entry.column] ?? "").filter((v) => v.trim() !== "");
              const selectId = `csv-map-${entry.column}`;
              const needsConfirm = !entry.confirmed && entry.field !== null;
              return (
                <tr key={entry.column}>
                  <th scope="row" className={`${TD} text-left font-medium text-gray-900 dark:text-gray-100`}>
                    {headers[entry.column] ?? entry.header}
                  </th>
                  <td className={`${TD} max-w-xs truncate text-gray-600 dark:text-gray-400`}>
                    {examples.length > 0 ? examples.join(" · ") : <span className="italic">empty</span>}
                  </td>
                  <td className={TD}>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-48 flex-1">
                        <label htmlFor={selectId} className="sr-only">
                          Field for column {entry.header}
                        </label>
                        <select
                          id={selectId}
                          value={entry.field ?? ""}
                          onChange={(event) =>
                            c.setField(entry.column, event.target.value === "" ? null : event.target.value)
                          }
                          className={SELECT}
                        >
                          <option value="">Do not import</option>
                          {target.fields.map((field) => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                              {field.required ? " (required)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      {needsConfirm && (
                        <>
                          <Chip tone="warn">Check this match ({Math.round(entry.confidence * 100)}%)</Chip>
                          <button
                            type="button"
                            className={BUTTON_SECONDARY}
                            onClick={() => c.confirm(entry.column)}
                            aria-label={`Confirm that ${entry.header} is imported as ${fieldLabels.get(entry.field as string) ?? entry.field}`}
                          >
                            Confirm
                          </button>
                        </>
                      )}
                      {entry.field !== null && entry.confirmed && entry.method !== "manual" && (
                        <span className={`text-xs ${MUTED}`}>Matched automatically</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {c.missingRequired.length > 0 && (
        <p role="alert" className="text-sm text-danger-strong">
          Choose a column for: {c.missingRequired.map((f) => f.label).join(", ")}.
        </p>
      )}
      {c.pendingConfirmations.length > 0 && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-warning-strong">
          {plural(c.pendingConfirmations.length, "match")} guessed from similar names. Confirm each one, or pick a
          different field.
          <button type="button" className={BUTTON_SECONDARY} onClick={c.confirmAll}>
            Confirm all
          </button>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — check rows
// ---------------------------------------------------------------------------

export function ValidateStep<T>({ c, target }: StepProps<T>) {
  const { validate, file } = c.state;
  const fieldLabels = fieldLabelMap(target);

  if (validate.status === "running" || validate.status === "idle") {
    return <Progress label={`Checking every row${file ? ` in ${file.name}` : ""}`} value={validate.progress} max={1} />;
  }
  if (validate.status === "error") {
    return (
      <ErrorPanel title="The rows could not be checked" message={validate.message ?? "Something went wrong."}>
        <button type="button" className={BUTTON_PRIMARY} onClick={() => void c.validate()}>
          Retry
        </button>
      </ErrorPanel>
    );
  }

  const result = validate.result;
  if (!result) return null;
  const problems = issueRows(result.errors);
  const warnings = issueRows(result.warnings);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Chip tone="good">{plural(result.validCount, "row")} ready</Chip>
        {result.errorCount > 0 && <Chip tone="bad">{plural(result.errorCount, "row")} with problems</Chip>}
        {result.warningCount > 0 && <Chip tone="warn">{plural(result.warningCount, "warning")}</Chip>}
        {result.blankRows > 0 && <Chip tone="neutral">{plural(result.blankRows, "empty row")} ignored</Chip>}
      </div>

      {result.total === 0 && (
        <ErrorPanel
          title="No rows to import"
          message="The file has a header but no data rows. Add rows in your spreadsheet and upload it again."
        />
      )}

      {problems.length > 0 && (
        <section aria-label="Rows with problems" className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rows with problems</h4>
          <ProblemTable caption="Rows with problems" problems={problems} fieldLabels={fieldLabels} />
          {result.errorsTruncated && (
            <p className={`text-xs ${MUTED}`}>The list is capped; more rows have problems than are shown.</p>
          )}
          <button
            type="button"
            className={BUTTON_SECONDARY}
            onClick={() => downloadReport(file?.name ?? "import", problems, fieldLabels)}
          >
            Download problem report
          </button>
        </section>
      )}

      {warnings.length > 0 && (
        <details className="text-sm">
          <summary className={`cursor-pointer font-medium text-warning-strong ${FOCUS} rounded`}>
            {plural(result.warningCount, "warning")} to look at
          </summary>
          <div className="mt-2">
            <ProblemTable caption="Warnings" problems={warnings} fieldLabels={fieldLabels} limit={50} />
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — review (server dry run)
// ---------------------------------------------------------------------------

function totalsLine(totals: Pick<BatchTotals, "created" | "updated" | "skipped">, entityName: string): string {
  // entityName is the plural label ("contacts"), so lead with it instead of "1 contacts would be created".
  const label = entityName.charAt(0).toUpperCase() + entityName.slice(1);
  const parts = [`${label}: ${n(totals.created)} would be created`];
  if (totals.updated > 0) parts.push(`${n(totals.updated)} updated`);
  if (totals.skipped > 0) parts.push(`${n(totals.skipped)} skipped`);
  return `${parts.join(", ")}.`;
}

const DEFAULT_DECISION_LABELS: Record<ImportRowDecision, string> = {
  update: "Update existing",
  create: "Create new anyway",
  skip: "Skip",
};

const BULK_DECISION_LABELS: Record<ImportRowDecision, string> = {
  update: "Update all",
  create: "Create all",
  skip: "Skip all",
};

const DECISION_ORDER: readonly ImportRowDecision[] = ["update", "create", "skip"];

function describeMatch(match: ImportDuplicateMatch, fieldLabels: FieldLabels): string {
  const on =
    match.field || match.value
      ? ` (${[match.field ? (fieldLabels.get(match.field) ?? match.field) : null, match.value].filter(Boolean).join(": ")})`
      : "";
  if (match.source === "file") return `${match.matchLabel ?? "An earlier row in this file"}${on}`;
  const record =
    match.matchLabel ?? (match.matchId !== undefined && match.matchId !== null ? `#${match.matchId}` : null);
  return `${record ? `Existing record ${record}` : "An existing record"}${on}`;
}

function DuplicatesSection<T>({ c, target }: StepProps<T>) {
  const upsert = target.upsert;
  const { duplicates, decisions } = c.state.upsert;
  const fieldLabels = fieldLabelMap(target);
  if (!upsert || duplicates.length === 0) return null;

  const labels = { ...DEFAULT_DECISION_LABELS, ...upsert.decisionLabels };
  const offered = DECISION_ORDER.filter((d) => duplicates.some((m) => allowedDecisions(m).includes(d)));
  const keys = upsert.dedupeKeys.map((key) => fieldLabels.get(key) ?? key);
  const shown = duplicates.slice(0, 100);

  return (
    <section aria-label="Possible duplicates" className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Possible duplicates</h4>
      <p className={`text-sm ${BODY}`}>
        {plural(duplicates.length, "row")} {duplicates.length === 1 ? "looks" : "look"} like{" "}
        {duplicates.length === 1 ? "a duplicate" : "duplicates"}
        {keys.length > 0 ? `, matched on ${keys.join(" or ")}` : ""}. Choose what to do with each one. Nothing is
        overwritten unless you pick {labels.update}.
      </p>
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className={`mb-1 text-xs ${MUTED}`}>Apply to every possible duplicate</legend>
        {offered.map((decision) => (
          <button key={decision} type="button" className={BUTTON_SECONDARY} onClick={() => c.setAllDecisions(decision)}>
            {BULK_DECISION_LABELS[decision]}
          </button>
        ))}
      </fieldset>
      <div className="max-h-72 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full">
          <caption className="sr-only">Possible duplicates</caption>
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" className={TH}>
                Row
              </th>
              <th scope="col" className={TH}>
                Matches
              </th>
              <th scope="col" className={TH}>
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {shown.map((match) => {
              const selectId = `csv-decision-${match.row}`;
              return (
                <tr key={match.row}>
                  <td className={TD}>{match.row}</td>
                  <td className={TD}>{describeMatch(match, fieldLabels)}</td>
                  <td className={TD}>
                    <label htmlFor={selectId} className="sr-only">
                      Action for row {match.row}
                    </label>
                    <select
                      id={selectId}
                      value={decisions[match.row] ?? "skip"}
                      onChange={(event) => c.setDecision(match.row, event.target.value as ImportRowDecision)}
                      className={SELECT}
                    >
                      {allowedDecisions(match).map((decision) => (
                        <option key={decision} value={decision}>
                          {labels[decision]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {duplicates.length > shown.length && (
        <p className={`text-xs ${MUTED}`}>
          Showing the first {n(shown.length)} of {n(duplicates.length)} possible duplicates. The buttons above set the
          action for all of them.
        </p>
      )}
    </section>
  );
}

export function ReviewStep<T>({ c, target }: StepProps<T>) {
  const { dryRun, commit, validate, skipInvalid, file } = c.state;
  const fieldLabels = fieldLabelMap(target);

  if (commit.status === "running") {
    return (
      <Progress
        label={`Saving ${target.entityName}: ${n(commit.done)} of ${n(commit.total)}`}
        value={commit.done}
        max={Math.max(commit.total, 1)}
      />
    );
  }
  if (commit.status === "error") {
    return (
      <ErrorPanel title="The import did not finish" message={commit.message ?? "Something went wrong."}>
        <button type="button" className={BUTTON_PRIMARY} onClick={() => void c.runCommit()}>
          Retry
        </button>
      </ErrorPanel>
    );
  }
  if (dryRun.status === "running" || dryRun.status === "idle") {
    return (
      <Progress
        label={`Checking ${target.entityName} with the server: ${n(dryRun.done)} of ${n(dryRun.total)}. Nothing is saved yet.`}
        value={dryRun.done}
        max={Math.max(dryRun.total, 1)}
      />
    );
  }
  if (dryRun.status === "error") {
    return (
      <ErrorPanel title="The server check did not finish" message={dryRun.message ?? "Something went wrong."}>
        <button type="button" className={BUTTON_PRIMARY} onClick={() => void c.runDryRun()}>
          Retry
        </button>
      </ErrorPanel>
    );
  }

  const totals = dryRun.totals as BatchTotals;
  const serverProblems = serverRows(totals.errors);
  const localProblems = c.invalidRows;
  // Equals importableRows.length unless the target opted into upsert and the user skipped duplicates.
  const toImport = c.writeCount;
  const duplicateCount = c.upsertEnabled ? c.state.upsert.duplicates.length : 0;
  // Upsert: the plan follows the user's decisions; otherwise the server's dry-run totals.
  const plan = c.upsertEnabled
    ? {
        created: c.importableRows.length - duplicateCount + c.decisionCounts.create,
        updated: c.decisionCounts.update,
        skipped: c.decisionCounts.skip,
      }
    : totals;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Chip tone="good">{plural(toImport, "row")} ready to import</Chip>
        {duplicateCount > 0 && <Chip tone="warn">{plural(duplicateCount, "possible duplicate")}</Chip>}
        {localProblems > 0 && <Chip tone="bad">{plural(localProblems, "row")} failed the checks</Chip>}
        {serverProblems.length > 0 && (
          <Chip tone="bad">{plural(c.rejectedRows.size, "row")} refused by the server</Chip>
        )}
      </div>
      <p className={`text-sm ${BODY}`}>{totalsLine(plan, target.entityName)} Nothing has been saved yet.</p>

      <DuplicatesSection c={c} target={target} />

      {serverProblems.length > 0 && (
        <section aria-label="Rows the server refused" className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rows the server refused</h4>
          <ProblemTable caption="Rows the server refused" problems={serverProblems} fieldLabels={fieldLabels} />
          <button
            type="button"
            className={BUTTON_SECONDARY}
            onClick={() => downloadReport(file?.name ?? "import", serverProblems, fieldLabels)}
          >
            Download problem report
          </button>
        </section>
      )}

      {c.problemCount > 0 &&
        (c.allowSkipInvalid ? (
          <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-warning-strong dark:border-amber-700 dark:bg-amber-900/20">
            <input
              type="checkbox"
              checked={skipInvalid}
              onChange={(event) => c.setSkipInvalid(event.target.checked)}
              className={`mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 ${FOCUS}`}
            />
            <span>
              Skip the {plural(c.problemCount, "row")} with problems and import the other {n(toImport)}. The skipped
              rows are not saved.
            </span>
          </label>
        ) : (
          <p role="alert" className="text-sm text-danger-strong">
            Fix the {plural(c.problemCount, "row")} with problems in your file and upload it again. Nothing was
            imported.
          </p>
        ))}
      {validate.result && validate.result.warningCount > 0 && (
        <p className={`text-xs ${MUTED}`}>
          {plural(validate.result.warningCount, "warning")} from the check{" "}
          {validate.result.warningCount === 1 ? "is kept as a note and does" : "are kept as notes and do"} not block the
          import.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — done
// ---------------------------------------------------------------------------

export function DoneStep<T>({ c, target }: StepProps<T>) {
  const { commit, file } = c.state;
  const totals = commit.totals;
  const fieldLabels = fieldLabelMap(target);
  const leftOut = c.invalidRows + c.rejectedRows.size;
  const commitProblems = serverRows(totals.errors);

  return (
    <div className="space-y-4">
      <output className="block text-sm font-medium text-success-strong">
        Imported {plural(totals.created, "new record")}
        {totals.updated > 0 ? `, updated ${n(totals.updated)}` : ""}
        {totals.skipped > 0 ? `, skipped ${n(totals.skipped)}` : ""}.
      </output>
      {leftOut > 0 && (
        <p className={`text-sm ${BODY}`}>
          {plural(leftOut, "row")} {leftOut === 1 ? "was" : "were"} left out because of problems. Fix them in your file
          and import that file again to add them.
        </p>
      )}
      {commitProblems.length > 0 && (
        <section aria-label="Rows that failed during the import" className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rows that failed during the import</h4>
          <ProblemTable
            caption="Rows that failed during the import"
            problems={commitProblems}
            fieldLabels={fieldLabels}
          />
          <button
            type="button"
            className={BUTTON_SECONDARY}
            onClick={() => downloadReport(file?.name ?? "import", commitProblems, fieldLabels)}
          >
            Download problem report
          </button>
        </section>
      )}
    </div>
  );
}
